import { getConfig } from '@barbanet/api-service'
import {
	UserStatusCheckResponse,
	useUserStatusCheck
} from '@barbanet/user-status-check'

import { useExceptionHandler } from '@barbanet/ui-kit/composables'

import { vehicleInfoService } from '../domains/user/services/driver'
import {
	IFormStatusEnum,
	IHeavyTruckInfo,
	ILightCommercialVehicleFleetInfo,
	ILightCommercialVehicleInfo,
	IMotorcycleFleetInfo,
	IMotorcycleInfo
} from '../domains/user/types/driver'
import { VehicleInfoResponse } from '../domains/user/types/dto/driver'

export interface UiInstructions {
	title: string
	message: string
	submitButton?: {
		text: string
		onClick: () => void
	}
	dismissText?: string
}
export interface CheckOptions {
	callback?: () => unknown | void
	passStates?: IFormStatusEnum | IFormStatusEnum[]
}

const ttl = 15 * 60 * 1000

const vehicleInfo = ref<Nullable<VehicleInfoResponse>>(null)
const userStatusInfo = ref<Nullable<UserStatusCheckResponse>>(null)
const lastUpdate = ref<Nullable<Date>>(null)

const checking = ref(false)

const modalVisible = ref(false)
const uiInstructions = ref<Nullable<UiInstructions>>(null)

const isCacheValid = () => {
	if (!userStatusInfo.value || !vehicleInfo.value || !lastUpdate.value) {
		return false
	}

	const now = new Date()
	const difference = now.getTime() - lastUpdate.value.getTime()

	return difference > 0 && difference < ttl
}

export function useDriverStatus() {
	const { t } = useI18n()
	const userStatus = useUserStatusCheck()
	const router = useRouter()

	const getInfo = async () => {
		if (isCacheValid()) {
			return {
				userStatusInfo: userStatusInfo.value,
				vehicleInfo: vehicleInfo.value,
				fromCache: true
			}
		}

		const apiServiceConfig = getConfig()
		if (
			!apiServiceConfig ||
			!apiServiceConfig.credentials ||
			!apiServiceConfig.credentials.accessToken
		) {
			console.warn('[DriverStatus] credentials is not available')
			return null
		}

		checking.value = true

		const userStatusResponse = await userStatus.check()
		if (!userStatusResponse) {
			checking.value = false
			return null
		}

		try {
			const vehicleInfoResponse = await vehicleInfoService()

			if (!vehicleInfoResponse || !vehicleInfoResponse.data) {
				return null
			}

			lastUpdate.value = new Date()
			userStatusInfo.value = userStatusResponse.content
			vehicleInfo.value = vehicleInfoResponse.data.content

			return {
				userStatusInfo: userStatusInfo.value,
				vehicleInfo: vehicleInfo.value,
				fromCache: false
			}
		} catch (error) {
			useExceptionHandler(error)
			return null
		} finally {
			checking.value = false
		}
	}

	const parseHeavyVehicleRejectedMessage = (
		info: Required<IHeavyTruckInfo>
	) => {
		const imagesWithIssue: string[] = []

		if (info.driverInfo.status === IFormStatusEnum.REJECTED) {
			if (!info.driverInfo.profileImageInfo) {
				imagesWithIssue.push(t('shared.driverProfileImage'))
			}
		}

		if (info.fleetInfo.status === IFormStatusEnum.REJECTED) {
			if (!info.fleetInfo.smartCardFrontImageInfo) {
				imagesWithIssue.push(t('shared.smartCardFrontImage'))
			}
		}

		const images = imagesWithIssue.length
			? imagesWithIssue.length === 1
				? imagesWithIssue[0]
				: imagesWithIssue
						.map(
							(item, index) =>
								`${item}${index < imagesWithIssue.length - 1 ? (imagesWithIssue.length > 2 && index < imagesWithIssue.length - 2 ? t('helper.comma') : ` ${t('helper.and')} `) : ''}`
						)
						.join('')
			: null

		if (images) {
			uiInstructions.value = {
				title: t('shared.completeCredentials'),
				message: t('driver-status.rejectedImageTemplate', { images }),
				submitButton: {
					text: t('shared.upload'),
					onClick: () => {
						router.push({
							name: 'CredentialsPage',
							query: { from: 'driver-status-check' }
						})
					}
				},
				dismissText: t('shared.later')
			}
		} else {
			console.warn(
				'[DriverStatus] an unexpected scenario happening in REJECTED state'
			)
		}
	}

	const parseHeavyVehicleExpiredMessage = (info: Required<IHeavyTruckInfo>) => {
		const expiredFields: string[] = []

		if (info.driverInfo.status === IFormStatusEnum.EXPIRED) {
			if (!info.driverInfo.licenseExpirationDate) {
				expiredFields.push(t('shared.licenseExpiry'))
			}

			if (!info.driverInfo.smartCardExpirationDate) {
				expiredFields.push(t('shared.smartCardExpiry'))
			}
		}

		if (info.fleetInfo.status === IFormStatusEnum.EXPIRED) {
			if (!info.fleetInfo.technicalInspectionDate) {
				expiredFields.push(t('shared.technicalInspectionDate'))
			}
		}

		const fields = expiredFields.length
			? expiredFields.length === 1
				? expiredFields[0]
				: expiredFields
						.map(
							(item, index) =>
								`${item}${index < expiredFields.length - 1 ? (expiredFields.length > 2 && index < expiredFields.length - 2 ? t('helper.comma') : ` ${t('helper.and')} `) : ''}`
						)
						.join('')
			: null

		if (fields) {
			uiInstructions.value = {
				title: t('shared.completeCredentials'),
				message: t('driver-status.heavyVehicleExpiredTemplate', { fields }),
				submitButton: {
					text: t('shared.understand'),
					onClick() {
						modalVisible.value = false
					}
				}
			}
		} else {
			console.warn(
				'[DriverStatus] an unexpected scenario happening in EXPIRED state'
			)
		}
	}

	const parseNonHeavyVehicleRejectedMessage = (
		info: Required<IMotorcycleInfo | ILightCommercialVehicleInfo>
	) => {
		const imagesWithIssue: string[] = []
		const fieldsWithIssue: string[] = []

		if (info.driverInfo.status === IFormStatusEnum.REJECTED) {
			if (
				!info.driverInfo.licenseFrontImageInfo ||
				!info.driverInfo.licenseBackImageInfo
			) {
				imagesWithIssue.push(t('driver-status.drivingLicense'))
			}

			if (!info.driverInfo.profileImageInfo) {
				imagesWithIssue.push(t('shared.driverProfileImage'))
			}
		}

		if (info.fleetInfo.status === IFormStatusEnum.REJECTED) {
			// Check possible rejected images
			if (
				!info.fleetInfo.vehicleImageInfos ||
				!info.fleetInfo.vehicleImageInfos.length
			) {
				if (info.type === 'LIGHT_COMMERCIAL_VEHICLE') {
					imagesWithIssue.push(t('shared.vehicleImages'))
				} else {
					const { boxType, boxHeight, boxLength, boxWidth } =
						info.fleetInfo as IMotorcycleFleetInfo

					// Check if motorcycle has box by checking any box related fields are filled
					// FIXME: this may have some issues because backend dos not store haveBox or something
					if (
						boxType === undefined ||
						boxType === null ||
						boxHeight ||
						boxLength ||
						boxWidth
					) {
						imagesWithIssue.push(t('shared.motorImage'))
					}
				}
			}

			if (
				!info.fleetInfo.vehicleCardFrontImageInfo ||
				!info.fleetInfo.vehicleCardBackImageInfo
			) {
				imagesWithIssue.push(
					info.type === 'LIGHT_COMMERCIAL_VEHICLE'
						? t('driver-status.vehicleCard')
						: t('driver-status.motorcycleCard')
				)
			}

			// Check possible rejected fields based on type
			if (info.type === 'LIGHT_COMMERCIAL_VEHICLE') {
				const { cargoBedLength, cargoBedWidth, cargoBedHeight } =
					info.fleetInfo as ILightCommercialVehicleFleetInfo

				if (!cargoBedLength) {
					fieldsWithIssue.push(t('shared.loaderLength'))
				}

				if (!cargoBedWidth) {
					fieldsWithIssue.push(t('shared.loaderWidth'))
				}

				if (!cargoBedHeight) {
					fieldsWithIssue.push(t('shared.loaderHeight'))
				}
			} else {
				const { boxType, boxLength, boxWidth, boxHeight, vehicleImageInfos } =
					info.fleetInfo as IMotorcycleFleetInfo

				// Check if motorcycle has box by checking any box related fields are filled
				// FIXME: this may have some issues because backend dos not store haveBox or something
				if (
					boxType ||
					boxLength ||
					boxWidth ||
					boxHeight ||
					(vehicleImageInfos && vehicleImageInfos.length)
				) {
					if (!boxLength) {
						fieldsWithIssue.push(t('shared.boxLength'))
					}

					if (!boxWidth) {
						fieldsWithIssue.push(t('shared.boxWidth'))
					}

					if (!boxHeight) {
						fieldsWithIssue.push(t('shared.boxHeight'))
					}
				}
			}
		}

		const images = imagesWithIssue.length
			? imagesWithIssue.length === 1
				? imagesWithIssue[0]
				: imagesWithIssue
						.map(
							(item, index) =>
								`${item}${index < imagesWithIssue.length - 1 ? (imagesWithIssue.length > 2 && index < imagesWithIssue.length - 2 ? t('helper.comma') : ` ${t('helper.and')} `) : ''}`
						)
						.join('')
			: null

		const fields = fieldsWithIssue.length
			? fieldsWithIssue.length === 1
				? fieldsWithIssue[0]
				: fieldsWithIssue
						.map(
							(item, index) =>
								`${item}${index < fieldsWithIssue.length - 1 ? (fieldsWithIssue.length > 2 && index < fieldsWithIssue.length - 2 ? t('helper.comma') : ` ${t('helper.and')} `) : ''}`
						)
						.join('')
			: null

		let message = ''
		let submitText = t('driver-status.enterInformation')

		if (images && fields) {
			message = t('driver-status.rejectedMixedTemplate', { images, fields })
			submitText = t('shared.completeCredentials')
		} else if (images) {
			submitText = t('shared.upload')
			message = t('driver-status.rejectedImageTemplate', { images })
		} else if (fields) {
			submitText = t('driver-status.enterInformation')
			message = t('driver-status.rejectedFieldTemplate', { fields })
		}

		uiInstructions.value = {
			title: t('shared.completeCredentials'),
			message,
			submitButton: {
				text: submitText,
				onClick: () => {
					router.push({
						name: 'CredentialsPage',
						query: { from: 'driver-status-check' }
					})
				}
			},
			dismissText: t('shared.later')
		}
	}

	const parseNonHeavyVehicleExpiredMessage = (
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		_info: Required<IMotorcycleInfo | ILightCommercialVehicleInfo>
	) => {
		// TODO: implement this later, backend logic is not completed yet
	}

	const check = async (options: CheckOptions = {}) => {
		const info = await getInfo()

		if (!info || !info.vehicleInfo || !info.userStatusInfo) {
			return null
		}

		const { vehicleInfo, userStatusInfo, fromCache } = info

		if (userStatusInfo.status !== 1) {
			// User not complete authentication with HODA
			// so the userCheckStatus composable take care of it to show ui instructions
			// we just check that if info coming from our cache, call check user status one more time
			if (fromCache) {
				userStatus.check()
			}
			return false
		}

		// User is active and we just check the vehicle status
		const _passStates = new Set([IFormStatusEnum.ACTIVE])
		if (options.passStates) {
			if (Array.isArray(options.passStates)) {
				options.passStates.forEach((item) => _passStates.add(item))
			} else {
				_passStates.add(options.passStates)
			}
		}
		const passStates = Array.from(_passStates)

		if (
			vehicleInfo.infos.length === 0 ||
			vehicleInfo.infos[0].status === undefined
		) {
			// Driver not yet start to complete any credentials
			uiInstructions.value = {
				title: t('shared.completeCredentials'),
				message: t('driver-status.noCredentialProvidedMessage'),
				dismissText: t('shared.later'),
				submitButton: {
					text: t('shared.completeCredentials'),
					onClick() {
						router.push({
							name: 'CredentialsPage',
							query: { from: 'driver-status-check' }
						})
					}
				}
			}
			modalVisible.value = true
			return false
		}

		const currentVehicleInfo = vehicleInfo.infos[0] as Required<
			VehicleInfoResponse['infos'][0]
		>

		if (passStates.includes(currentVehicleInfo.status)) {
			if (options.callback !== undefined) {
				options.callback()
			}

			return true
		}

		switch (currentVehicleInfo.status) {
			case IFormStatusEnum.INQUIRY:
				uiInstructions.value = {
					title: t('driver-status.inquiring.title'),
					message: t('driver-status.inquiring.message'),
					submitButton: {
						text: t('shared.understand'),
						onClick() {
							modalVisible.value = false
						}
					}
				}
				break
			case IFormStatusEnum.PENDING:
				uiInstructions.value = {
					title: t('shared.completeCredentials'),
					message: t('driver-status.pendingCredentialsMessage'),
					dismissText: t('shared.later'),
					submitButton: {
						text: t('shared.completeCredentials'),
						onClick() {
							router.push({
								name: 'CredentialsPage',
								query: { from: 'driver-status-check' }
							})
						}
					}
				}
				break
			case IFormStatusEnum.WAITING_TO_ACCEPT_ADMIN:
				uiInstructions.value = {
					title: t('driver-status.waitingToAcceptAdmin.title'),
					message: t('driver-status.waitingToAcceptAdmin.message'),
					submitButton: {
						text: t('shared.understand'),
						onClick() {
							modalVisible.value = false
						}
					}
				}
				break
			case IFormStatusEnum.REJECTED:
				if (currentVehicleInfo.type === 'HEAVY_DUTY_TRUCK') {
					parseHeavyVehicleRejectedMessage(currentVehicleInfo)
				} else {
					parseNonHeavyVehicleRejectedMessage(currentVehicleInfo)
				}
				break
			case IFormStatusEnum.EXPIRED:
				if (currentVehicleInfo.type === 'HEAVY_DUTY_TRUCK') {
					parseHeavyVehicleExpiredMessage(currentVehicleInfo)
				} else {
					parseNonHeavyVehicleExpiredMessage(currentVehicleInfo)
				}
				break
			case IFormStatusEnum.DOCUMENT_INFO_NOTFOUND:
				if (
					currentVehicleInfo.type === 'HEAVY_DUTY_TRUCK' &&
					currentVehicleInfo.fleetInfo.status ===
						IFormStatusEnum.DOCUMENT_INFO_NOTFOUND
				) {
					uiInstructions.value = {
						title: t('driver-status.documentNotFound.title'),
						message: t('driver-status.documentNotFound.message'),
						submitButton: {
							text: t('shared.understand'),
							onClick() {
								modalVisible.value = false
							}
						}
					}
				} else {
					console.warn(
						'[DriverStatus] an unexpected scenario happening in DOCUMENT_INFO_NOTFOUND state'
					)
				}
				break
			case IFormStatusEnum.INACTIVE:
				uiInstructions.value = {
					title: t('driver-status.inactive.title'),
					message: t('driver-status.inactive.message'),
					submitButton: {
						text: t('shared.understand'),
						onClick() {
							modalVisible.value = false
						}
					}
				}
				break
		}

		modalVisible.value = true
		return false
	}

	return {
		cache: {
			lastUpdate,
			userStatusInfo,
			vehicleInfo
		},
		checking,
		check,
		modalVisible,
		uiInstructions
	}
}
